import type { AudioFile } from './types.js';
import type { HttpClient } from './http.js';

const MAX_AUDIO_BYTES = 52_428_800;

export function createAudioApi(http: HttpClient) {
  return {
    async list(): Promise<AudioFile[]> {
      const data = await http.json<{ files: AudioFile[] }>('/api/audio/');
      return data.files;
    },

    async upload(file: File): Promise<AudioFile> {
      if (file.size > MAX_AUDIO_BYTES) {
        throw new Error('File size must be less than 50MB');
      }

      const formData = new FormData();
      formData.append('file', file);

      const response = await http.fetch('/api/audio/', {
        method: 'POST',
        body: formData,
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(
          (data as { error?: string }).error ?? 'Failed to upload audio file',
        );
      }

      return (data as { file: AudioFile }).file;
    },

    contentPathByStoragePath(storagePath: string): string {
      return `/api/audio/by-path/content?path=${encodeURIComponent(storagePath)}`;
    },

    contentPathById(id: string): string {
      return `/api/audio/${encodeURIComponent(id)}/content`;
    },

    async fetchContentByPath(storagePath: string): Promise<ArrayBuffer> {
      const response = await http.fetch(this.contentPathByStoragePath(storagePath));
      if (!response.ok) {
        throw new Error(`Failed to fetch audio: ${response.status}`);
      }
      return response.arrayBuffer();
    },

    async delete(id: string): Promise<void> {
      await http.json(`/api/audio/${id}`, { method: 'DELETE' });
    },
  };
}
